import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogIn, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";

export default function Attendance() {
  const { t } = useLang();
  const { user } = useAuth();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [mine, setMine] = useState(null);
  const [summary, setSummary] = useState(null);

  const load = async () => {
    const r = await api.get("/punch/my", { params: { month } });
    setMine(r.data);
    if (user.role === "admin") {
      const s = await api.get("/punch/summary", { params: { month } });
      setSummary(s.data);
    }
  };
  useEffect(() => { load().catch(() => {}); }, [month]);

  const [locating, setLocating] = useState(false);

  const punch = () => {
    if (!navigator.geolocation) {
      toast.error(t("gpsRequired"));
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await api.post("/punch", { lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy });
          toast.success(`${t("punchSuccess")}: ${r.data.action === "in" ? t("inTime") : t("outTime")} ${r.data.time}`);
          load();
        } catch (err) {
          toast.error(err.response?.data?.detail || t("error"));
        } finally {
          setLocating(false);
        }
      },
      () => {
        setLocating(false);
        toast.error(t("gpsRequired"));
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const openPunch = mine?.records?.find((r) => !r.out_time && r.date === new Date().toISOString().slice(0, 10));

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-['Outfit'] text-2xl font-bold text-[#165A2E] md:text-3xl">{t("attendance")}</h1>
        <div className="flex items-center gap-3">
          <Input data-testid="attendance-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" />
          <Button onClick={punch} disabled={locating} data-testid="punch-btn" className={`rounded-full ${openPunch ? "bg-red-500 text-white hover:bg-red-600" : "bg-[#84CC16] text-[#0A0A0A] hover:bg-[#65A30D]"}`}>
            {openPunch ? <LogOut size={16} className="mr-1" /> : <LogIn size={16} className="mr-1" />}
            {locating ? t("gpsLocating") : openPunch ? t("punchOut") : t("punchIn")}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="mine">
        <TabsList>
          <TabsTrigger value="mine" data-testid="tab-my-punches">{t("myPunches")}</TabsTrigger>
          {user.role === "admin" && <TabsTrigger value="summary" data-testid="tab-hr-summary">{t("summary")}</TabsTrigger>}
        </TabsList>
        <TabsContent value="mine">
          {mine && (
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="gfa-card p-5"><p className="text-sm text-slate-500">{t("hours")}</p><p className="font-['Outfit'] text-2xl font-bold text-[#165A2E]" data-testid="my-hours">{mine.total_hours}</p></div>
              <div className="gfa-card p-5"><p className="text-sm text-slate-500">{t("allowanceRate")}</p><p className="font-['Outfit'] text-2xl font-bold text-[#165A2E]">RM {mine.allowance_rate}</p></div>
              <div className="gfa-card p-5"><p className="text-sm text-slate-500">{t("allowance")}</p><p className="font-['Outfit'] text-2xl font-bold text-emerald-600" data-testid="my-allowance">RM {mine.allowance.toFixed(2)}</p></div>
            </div>
          )}
          <div className="gfa-card overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("date")}</TableHead>
                  <TableHead>{t("inTime")}</TableHead>
                  <TableHead>{t("outTime")}</TableHead>
                  <TableHead>{t("hours")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(mine?.records || []).map((r) => (
                  <TableRow key={r.punch_id} data-testid={`punch-row-${r.punch_id}`}>
                    <TableCell>{r.date}</TableCell>
                    <TableCell>{r.in_time}</TableCell>
                    <TableCell>{r.out_time || "—"}</TableCell>
                    <TableCell>{r.hours}</TableCell>
                  </TableRow>
                ))}
                {(!mine || mine.records.length === 0) && (
                  <TableRow><TableCell colSpan={4} className="py-8 text-center text-slate-400">{t("noData")}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        {user.role === "admin" && (
          <TabsContent value="summary">
            <div className="gfa-card overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("name")}</TableHead>
                    <TableHead>{t("hours")}</TableHead>
                    <TableHead>{t("allowance")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(summary?.coaches || []).map((c) => (
                    <TableRow key={c.user_id} data-testid={`summary-row-${c.user_id}`}>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell>{c.hours}</TableCell>
                      <TableCell className="font-semibold text-emerald-600">RM {c.allowance.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  {(!summary || summary.coaches.length === 0) && (
                    <TableRow><TableCell colSpan={3} className="py-8 text-center text-slate-400">{t("noData")}</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
